import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Retro 8-bit spin wheel for picking a giveaway winner.
 *
 * Props:
 *   entries:   Array<{ _id, name, email, entry_number, is_winner? }>
 *   onWinner:  (winnerEntry) => void   // fires AFTER the spin animation lands
 *   onClose:   () => void
 */
const PALETTE = ['#2C6E49', '#FFC857', '#4AE54A', '#D62246', '#0F0F0F', '#FF6B35'];
const TEXT_ON = ['#FEFEE3', '#0a0a0a', '#0a0a0a', '#FEFEE3', '#FFC857', '#0a0a0a'];

// Crypto-strong random in [0, n)
const rand = (n) => {
  if (window.crypto && window.crypto.getRandomValues) {
    const a = new Uint32Array(1);
    window.crypto.getRandomValues(a);
    return a[0] % n;
  }
  return Math.floor(Math.random() * n);
};

// Cubic ease-out for the spin deceleration
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const SpinWheel = ({ entries = [], onWinner, onClose }) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const rotationRef = useRef(0);
  const animRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const [committedWinner, setCommittedWinner] = useState(null);
  const [size, setSize] = useState(420);
  const [emailing, setEmailing] = useState(false);
  const [emailedAt, setEmailedAt] = useState(null);
  const [emailError, setEmailError] = useState('');

  // Filter out anyone who already won so they can't win again
  const eligible = entries.filter((e) => !e.is_winner);
  const wedgeCount = Math.max(eligible.length, 1);
  const wedgeAngle = (Math.PI * 2) / wedgeCount;

  // --- DRAW ---
  const drawWheel = useCallback((rotation) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const cssSize = size;
    canvas.width = cssSize * dpr;
    canvas.height = cssSize * dpr;
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = cssSize / 2;
    const cy = cssSize / 2;
    const R = cssSize / 2 - 10;

    ctx.clearRect(0, 0, cssSize, cssSize);

    // Outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, R + 6, 0, Math.PI * 2);
    ctx.fillStyle = '#0a0a0a';
    ctx.fill();

    // Wedges
    for (let i = 0; i < wedgeCount; i++) {
      const start = rotation + i * wedgeAngle - Math.PI / 2;
      const end = start + wedgeAngle;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, start, end);
      ctx.closePath();
      ctx.fillStyle = PALETTE[i % PALETTE.length];
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#0a0a0a';
      ctx.stroke();

      // Label
      const e = eligible[i];
      if (e) {
        const mid = start + wedgeAngle / 2;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(mid);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        const fname = (e.name || 'PLAYER').toString().split(' ')[0].slice(0, 12).toUpperCase();
        ctx.fillStyle = TEXT_ON[i % TEXT_ON.length];
        ctx.font = `bold ${Math.max(10, Math.min(14, cssSize / wedgeCount * 1.6))}px 'Press Start 2P', 'IBM Plex Mono', monospace`;
        // Don't render text if wedges are too small
        if (wedgeCount <= 24) {
          ctx.fillText(fname, R - 14, 0);
        } else {
          ctx.font = `bold 10px 'IBM Plex Mono', monospace`;
          ctx.fillText(`#${e.entry_number ?? i + 1}`, R - 14, 0);
        }
        ctx.restore();
      }
    }

    // Hub
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fillStyle = '#FFC857';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#0a0a0a';
    ctx.stroke();
    ctx.fillStyle = '#0a0a0a';
    ctx.font = "bold 11px 'Press Start 2P', monospace";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', cx, cy + 1);
  }, [eligible, wedgeCount, wedgeAngle, size]);

  // Resize canvas based on viewport
  useEffect(() => {
    const onResize = () => {
      if (!wrapperRef.current) return;
      const w = wrapperRef.current.clientWidth;
      const next = Math.max(260, Math.min(540, Math.floor(w - 20)));
      setSize(next);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Initial draw / redraw on size or entries change
  useEffect(() => {
    drawWheel(rotationRef.current);
  }, [drawWheel]);

  // Cleanup
  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  // --- SPIN ---
  const spin = useCallback(() => {
    if (spinning) return;
    if (eligible.length === 0) return;

    setWinner(null);
    setCommittedWinner(null);
    setEmailedAt(null);
    setEmailError('');
    setSpinning(true);

    const winnerIdx = rand(eligible.length);
    const targetEntry = eligible[winnerIdx];

    // Pointer is at angle -PI/2 (top). The wedge for index i occupies
    // [rotation + i*wedgeAngle - PI/2, rotation + (i+1)*wedgeAngle - PI/2].
    // We want the wedge midline to land under the pointer:
    //   rotation + i*wedgeAngle + wedgeAngle/2 - PI/2 = -PI/2   (mod 2PI)
    //   => rotation = -i*wedgeAngle - wedgeAngle/2 (mod 2PI)
    const baseTarget = -winnerIdx * wedgeAngle - wedgeAngle / 2;
    // Add 6-9 full rotations for drama
    const fullSpins = 6 + rand(4);
    const startRotation = rotationRef.current;
    // Normalize current rotation
    const startMod = ((startRotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const targetMod = ((baseTarget % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let delta = targetMod - startMod;
    if (delta < 0) delta += Math.PI * 2;
    const finalRotation = startRotation + fullSpins * Math.PI * 2 + delta;

    const duration = 5200 + rand(800); // 5.2 - 6.0s
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      rotationRef.current = startRotation + (finalRotation - startRotation) * eased;
      drawWheel(rotationRef.current);
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        rotationRef.current = finalRotation;
        drawWheel(rotationRef.current);
        setSpinning(false);
        setWinner(targetEntry);
      }
    };
    animRef.current = requestAnimationFrame(tick);
  }, [drawWheel, eligible, spinning, wedgeAngle]);

  const proclaimWinner = async () => {
    if (!winner) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/giveaway/winner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: winner._id }),
      });
      const data = await res.json();
      if (data && data.success) {
        setCommittedWinner(data.winner);
        if (onWinner) onWinner(data.winner);
      } else {
        alert('Failed to save winner. Please try again.');
      }
    } catch (e) {
      console.error(e);
      alert('Network error saving winner.');
    }
  };

  const emailWinner = async () => {
    if (!winner) return;
    if (emailing) return;
    const customMsg = window.prompt(
      'Optional custom message (leave blank to use the default template):',
      ''
    );
    if (customMsg === null) return; // user cancelled
    const prize = window.prompt('Prize description (shown in the email):', '$100 GIFT CARD') || '$100 GIFT CARD';
    setEmailing(true);
    setEmailError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/giveaway/winner/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_id: winner._id,
          prize,
          custom_message: customMsg || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data && data.success) {
        setEmailedAt(new Date().toISOString());
      } else {
        setEmailError(data?.detail || 'Failed to send email.');
      }
    } catch (e) {
      console.error(e);
      setEmailError('Network error sending email.');
    } finally {
      setEmailing(false);
    }
  };

  const noEntries = eligible.length === 0;

  return (
    <div
      data-testid="spin-wheel-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.88)', overflowY: 'auto',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 12px',
        fontFamily: "'IBM Plex Mono', 'Press Start 2P', monospace",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: '100%', maxWidth: '560px',
          background: '#0a0a0a', border: '4px solid #FFC857',
          boxShadow: '0 0 0 4px #000, 8px 8px 0 0 rgba(0,0,0,0.8)',
          padding: '20px 16px 24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ color: '#FFC857', fontSize: '11px', letterSpacing: '0.3em' }}>
            ★ GIVEAWAY ★
          </div>
          <button
            data-testid="spin-wheel-close"
            onClick={onClose}
            style={{
              background: 'transparent', border: '2px solid #666', color: '#FFC857',
              padding: '4px 10px', fontFamily: 'inherit', fontSize: '11px',
              letterSpacing: '0.18em', cursor: 'pointer',
            }}
          >
            CLOSE ✕
          </button>
        </div>

        <h2 style={{
          color: '#4AE54A', fontSize: 'clamp(18px, 4vw, 26px)', textAlign: 'center',
          margin: '8px 0 4px', letterSpacing: '0.08em',
          fontFamily: "'Press Start 2P', monospace",
        }}>
          SPIN THE WHEEL
        </h2>
        <p style={{ color: '#999', fontSize: '11px', textAlign: 'center', letterSpacing: '0.2em', marginBottom: '14px' }}>
          {eligible.length} eligible {eligible.length === 1 ? 'entry' : 'entries'}
        </p>

        {/* Wheel + pointer */}
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <canvas
            ref={canvasRef}
            data-testid="spin-wheel-canvas"
            style={{ display: 'block', maxWidth: '100%', imageRendering: 'auto' }}
          />
          {/* Pointer at top */}
          <div style={{
            position: 'absolute', top: '-2px', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '14px solid transparent', borderRight: '14px solid transparent',
            borderTop: '24px solid #FFC857',
            filter: 'drop-shadow(0 2px 0 #000)',
            pointerEvents: 'none',
          }} />
        </div>

        {/* Spin button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <button
            data-testid="spin-wheel-spin-btn"
            onClick={spin}
            disabled={spinning || noEntries}
            style={{
              padding: '14px 36px',
              background: spinning ? '#555' : noEntries ? '#444' : '#4AE54A',
              color: spinning ? '#999' : noEntries ? '#888' : '#0a0a0a',
              border: '3px solid #000', boxShadow: '5px 5px 0 0 #000',
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '14px', letterSpacing: '0.14em',
              cursor: spinning || noEntries ? 'not-allowed' : 'pointer',
              transition: 'transform 120ms',
            }}
          >
            {spinning ? '⟳ SPINNING...' : noEntries ? 'NO ENTRIES' : '▶ SPIN'}
          </button>
        </div>

        {/* Winner card */}
        {winner && !spinning && (
          <div
            data-testid="spin-wheel-winner-card"
            style={{
              marginTop: '24px', padding: '20px 18px',
              background: 'linear-gradient(180deg, #2C6E49, #1a4a2c)',
              border: '4px solid #FFC857', boxShadow: '0 0 0 3px #000, 6px 6px 0 0 #000',
              textAlign: 'center',
            }}
          >
            <div style={{
              fontSize: '12px', color: '#FFC857', letterSpacing: '0.3em',
              fontFamily: "'Press Start 2P', monospace", marginBottom: '10px',
            }}>
              ★ WINNER ★
            </div>
            <div style={{
              fontSize: 'clamp(20px, 5vw, 30px)', color: '#FEFEE3', fontWeight: 800,
              letterSpacing: '0.06em', marginBottom: '4px',
            }}>
              {(winner.name || 'PLAYER').toUpperCase()}
            </div>
            <div style={{ fontSize: '13px', color: '#FFC857', letterSpacing: '0.1em', marginBottom: '4px' }}>
              ENTRY #{winner.entry_number ?? '—'}
            </div>
            <div style={{ fontSize: '12px', color: '#cce6d4', wordBreak: 'break-all' }}>
              {winner.email}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
              {!committedWinner ? (
                <>
                  <button
                    data-testid="spin-wheel-proclaim"
                    onClick={proclaimWinner}
                    style={{
                      padding: '12px 22px', background: '#FFC857', color: '#0a0a0a',
                      border: '3px solid #000', boxShadow: '4px 4px 0 0 #000',
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: '12px', letterSpacing: '0.12em', cursor: 'pointer',
                    }}
                  >
                    ✓ PROCLAIM WINNER
                  </button>
                  <button
                    data-testid="spin-wheel-respin"
                    onClick={() => { setWinner(null); spin(); }}
                    style={{
                      padding: '12px 22px', background: 'transparent', color: '#FFC857',
                      border: '3px solid #FFC857', boxShadow: '4px 4px 0 0 #000',
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: '12px', letterSpacing: '0.12em', cursor: 'pointer',
                    }}
                  >
                    ↻ RE-SPIN
                  </button>
                </>
              ) : (
                <div style={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
                }}>
                  <div style={{
                    padding: '8px 16px', color: '#4AE54A', border: '2px solid #4AE54A',
                    fontFamily: "'Press Start 2P', monospace", fontSize: '10px',
                    letterSpacing: '0.14em',
                  }}>
                    ✓ SAVED TO HISTORY
                  </div>
                  {emailedAt ? (
                    <div
                      data-testid="spin-wheel-email-sent"
                      style={{
                        padding: '10px 18px', color: '#4AE54A', border: '3px solid #4AE54A',
                        background: 'rgba(74,229,74,0.08)',
                        fontFamily: "'Press Start 2P', monospace", fontSize: '11px',
                        letterSpacing: '0.14em', textAlign: 'center',
                      }}
                    >
                      ✉ EMAIL SENT
                    </div>
                  ) : (
                    <button
                      data-testid="spin-wheel-email-winner"
                      onClick={emailWinner}
                      disabled={emailing}
                      style={{
                        padding: '12px 22px',
                        background: emailing ? '#666' : '#4AE54A',
                        color: emailing ? '#ccc' : '#0a0a0a',
                        border: '3px solid #000', boxShadow: '4px 4px 0 0 #000',
                        fontFamily: "'Press Start 2P', monospace",
                        fontSize: '12px', letterSpacing: '0.12em',
                        cursor: emailing ? 'wait' : 'pointer',
                      }}
                    >
                      {emailing ? '✉ SENDING...' : '✉ EMAIL THIS WINNER'}
                    </button>
                  )}
                  {emailError && (
                    <div style={{
                      color: '#FF6B35', fontSize: '11px',
                      letterSpacing: '0.1em', textAlign: 'center', maxWidth: '320px',
                    }}>
                      {emailError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <p style={{
          color: '#666', fontSize: '10px', textAlign: 'center', marginTop: '20px',
          letterSpacing: '0.16em',
        }}>
          ENTRIES MARKED AS WINNERS ARE EXCLUDED FROM FUTURE SPINS
        </p>
      </div>
    </div>
  );
};

export default SpinWheel;
